import { PresentationMode } from '../../../../../Enums/PresentationMode';
import { CommissionModel } from '../../../../../Enums/CommissionModel';
import { createServerErrorMessage, hasFieldChangedValue, validateEmail } from '../../../../../Helpers/FormInputHelper';
import { formatMessage } from '../../../../../Localization/formatMessage';
import Messages from '../../../../../Localization/Messages';
import { ServiceClient } from '../../../../../Services/ServiceClient';
import { teamPageReducerTypes } from '../../../Reducer';
import { agentInfoFormId } from './AgentInfo';

export const onSubmit = (
  event,
  team,
  editedAgent,
  modalMode,
  setModalMode,
  doesTeamExist,
  agent,
  setErrors,
  setCanSubmit,
  closeView,
  navigate,
  teamPageDispatch
) => {
  event.preventDefault();

  const handleErrors = error => {
    setModalMode(PresentationMode.Error);
    setErrors([createServerErrorMessage(error, agentInfoFormId)]);
    setCanSubmit(false);
  };

  if (modalMode === PresentationMode.Edit) {
    ServiceClient.updateAgentInfo(
      { ...editedAgent, employerId: editedAgent.employer?.id },
      getChangedFieldsForAgent(editedAgent, agent),
      data => {
        teamPageDispatch({
          type: teamPageReducerTypes.updateTeamInfo,
          payload: {
            ...team,
            agents: getAgentListAfterUpdate(team.agents, agent.id, data, agent),
          },
        });
        closeView();
      },
      error => {
        handleErrors(error);
      }
    );
  } else if (doesTeamExist) {
    ServiceClient.createAgent(
      team.codePrefix,
      { ...editedAgent, employerId: editedAgent.employer?.id },
      data => {
        teamPageDispatch({
          type: teamPageReducerTypes.updateTeamInfo,
          payload: { ...team, agents: [...team.agents, { ...data, addressIsInherited: true }] },
        });
        closeView();
      },
      error => {
        handleErrors(error);
      }
    );
  } else {
    ServiceClient.createTeam(
      team.codePrefix,
      { ...team, agents: [{ ...editedAgent, employerId: editedAgent.employer?.id }] },
      data => {
        navigate(`/team/view/${team.codePrefix}`);
        closeView();
        teamPageDispatch({
          type: teamPageReducerTypes.updateStateAfterCreateTeam,
          team: data,
        });
      },
      error => {
        handleErrors(error);
      }
    );
  }
};

export const areFieldsValid = (editedAgent, isNameRequired) => {
  const errors = [];

  if (isNameRequired && (!editedAgent.name || editedAgent.name?.trim() === '')) {
    errors.push({});
  }

  if (!editedAgent.costCenterId) {
    errors.push({});
  }

  if (!!editedAgent.email && !validateEmail(editedAgent.email)) {
    errors.push({
      inputId: 'email',
      message: formatMessage(Messages.common_incorrectEmailFormat),
    });
  }

  if (!!editedAgent?.extension?.groupEmail && !validateEmail(editedAgent.extension.groupEmail)) {
    errors.push({
      inputId: 'groupEmail',
      message: formatMessage(Messages.common_incorrectEmailFormat),
    });
  }

  if (editedAgent.commissionModel === CommissionModel.AssurandorIIF && !editedAgent.employeeNumber) {
    errors.push({});
  }

  return errors;
};

export const getChangedFieldsForAgent = (editedAgent, agent) => {
  let sum = 0;
  if (hasFieldChangedValue(editedAgent?.costCenterId, agent.costCenter.id)) {
    sum = sum + 1;
  }
  if (hasFieldChangedValue(editedAgent?.email, agent.email)) {
    sum = sum + 2;
  }
  if (hasFieldChangedValue(editedAgent?.phone, agent.phone)) {
    sum = sum + 4;
  }
  if (hasFieldChangedValue(editedAgent?.additionalName, agent.additionalName)) {
    sum = sum + 8;
  }
  if (hasFieldChangedValue(editedAgent?.externalSalesCode, agent.externalSalesCode)) {
    sum = sum + 16;
  }
  if (hasFieldChangedValue(editedAgent?.canBePortfolioResponsible, agent.canBePortfolioResponsible)) {
    sum = sum + 32;
  }
  if (
    hasFieldChangedValue(editedAgent?.extension?.groupEmail, agent.extension?.groupEmail) ||
    hasFieldChangedValue(editedAgent?.extension?.isDigitalCustomer, agent.extension?.isDigitalCustomer) ||
    hasFieldChangedValue(editedAgent?.extension?.availableForXmlExport, agent.extension?.availableForXmlExport)
  ) {
    sum = sum + 64;
  }
  if (hasFieldChangedValue(editedAgent?.name, agent.name)) {
    sum = sum + 128;
  }
  if (hasFieldChangedValue(editedAgent?.employer?.id, agent.employer?.id)) {
    sum = sum + 256;
  }
  if (hasFieldChangedValue(editedAgent?.commissionEnabled, agent.commissionEnabled)) {
    sum = sum + 512;
  }
  if (hasFieldChangedValue(editedAgent?.mobile, agent.mobile)) {
    sum = sum + 1024;
  }
  if (hasFieldChangedValue(editedAgent?.employeeNumber, agent.employeeNumber)) {
    sum = sum + 2048;
  }
  if (hasFieldChangedValue(editedAgent?.commissionModel, agent.commissionModel)) {
    sum = sum + 4096;
  }
  return sum;
};

const getAgentListAfterUpdate = (agents, agentId, apiData, oldAgent) => {
  const filteredAgents = agents.filter(x => x.id !== agentId);

  if (apiData.created) {
    return [...filteredAgents, apiData.updated, getMissingDataForAgent(apiData.created, oldAgent)];
  }
  return [...filteredAgents, getMissingDataForAgent(apiData.updated, oldAgent)];
};

const getMissingDataForAgent = (newAgent, oldAgent) => {
  return { ...newAgent, address: oldAgent.address };
};

export const mapEmployerListForSelect = employers => {
  return [
    { key: 0, value: null, displayName: '' },
    ...employers.map(employer => {
      return { key: employer.id, value: employer.id, displayName: employer.name };
    }),
  ];
};

export const getEmployer = (employers, employerId) => {
  return employers.find(x => x.id.toString() === employerId);
};
